import type { RequestFn } from '../http';

/**
 * KIAN-Lagebericht: tägliche Gesamtübersicht über alle offenen Tickets.
 * Erzeugt wird nur bei Aufruf (kein Scheduler) — die Status-Route sagt, ob eine
 * neue Erzeugung gerade sinnvoll und erlaubt ist.
 */

export interface BriefingStats {
  openTotal: number;
  unassigned: number;
  assignedUntouched: number;
  inProgress: number;
  waiting: number;
  customerMissing: number;
  overdue: number;
  slaBreached: number;
  closedLast48h: number;
  /** Ältere Berichte haben diese Felder noch nicht. */
  openedLast24h?: number;
  closedLast24h?: number;
  netLast24h?: number;
  truncatedFromLlm: number;
  /** Injection-Scan der Quelldaten — ältere Berichte haben die Felder nicht. */
  injectionBlocked?: number;
  injectionFlagged?: number;
  llm?: {
    model: string | null;
    tokensIn: number | null;
    tokensOut: number | null;
    costEur: number | null;
  };
}

export interface DailyBriefing {
  id: string;
  briefingText: string;
  stats: BriefingStats;
  autoTriggered: boolean;
  triggeredByName: string | null;
  createdAt: string;
}

export interface BriefingStatus {
  latest: DailyBriefing | null;
  changesSinceLatest: number;
  /** Manuell erzeugte Berichte heute (Europe/Berlin); 07:00-Läufe zählen nicht. */
  manualUsedToday: number;
  manualDailyLimit: number;
  manualRemainingToday: number;
  /** Wer heute wann manuell angefordert hat — für alle sichtbar. */
  manualToday: Array<{ createdAt: string; triggeredByName: string | null }>;
  eligibleNow: boolean;
  generating: boolean;
}

export interface BriefingHistoryEntry {
  id: string;
  createdAt: string;
  autoTriggered: boolean;
  triggeredByName: string | null;
}

export interface TechnicianBriefingStats {
  technicianId: string;
  technicianName: string;
  openOwn: number;
  assignedUntouched: number;
  inProgress: number;
  waiting: number;
  overdue: number;
  slaBreached: number;
  stale: number;
  dueSoon: number;
  closedLast48h: number;
  unassignedLast48h: number;
  hoursLast24h: number;
  hoursLast7d: number;
  medianFirstResponseHours: number | null;
  firstResponseSampleSize: number;
  pendingReminders: number;
  pendingHandovers: number;
  openCallbacks: number;
  /** Ab v1.254: Termine, Buchungs-Check gestern, Chat-Inbox — ältere Briefings haben sie nicht. */
  appointmentsToday?: number;
  hoursYesterday?: number;
  lowBookingYesterday?: boolean;
  chatsAwaitingReply?: number;
  chatsUnassigned?: number;
  /** Injection-Scan der Quelldaten — ältere Briefings haben die Felder nicht. */
  injectionBlocked?: number;
  injectionFlagged?: number;
  truncatedFromLlm: number;
  llm?: {
    model: string | null;
    tokensIn: number | null;
    tokensOut: number | null;
    costEur: number | null;
  };
}

export interface TechnicianBriefing {
  id: string;
  technicianId: string;
  technicianName: string;
  briefingText: string;
  stats: TechnicianBriefingStats;
  autoTriggered: boolean;
  triggeredByName: string | null;
  createdAt: string;
}

export interface TechnicianBriefingStatus {
  technicianId: string;
  technicianName: string;
  hasWeclappLink: boolean;
  latest: TechnicianBriefing | null;
  changesSinceLatest: number;
  manualUsedToday: number;
  manualDailyLimit: number;
  manualRemainingToday: number;
  eligibleNow: boolean;
  generating: boolean;
}

export interface BriefingTechnician {
  id: string;
  displayName: string;
  role: string;
  hasWeclappLink: boolean;
}

export function createBriefingApi(request: RequestFn) {
  return {
    async getBriefingStatus() {
      return request<BriefingStatus>('/briefing/status');
    },

    async generateBriefing() {
      return request<{ briefing: DailyBriefing; unchanged?: boolean; shared?: boolean }>(
        '/briefing/generate',
        { method: 'POST', body: JSON.stringify({}), kianUsageInline: true },
      );
    },

    async getBriefingHistory() {
      return request<{ history: BriefingHistoryEntry[] }>('/briefing/history');
    },

    async getBriefingById(id: string) {
      return request<{ briefing: DailyBriefing }>(`/briefing/${id}`);
    },

    async getBriefingTechnicians() {
      return request<{ technicians: BriefingTechnician[] }>('/briefing/technicians');
    },

    async getPersonalBriefingStatus(userId?: string) {
      const path = userId ? `/briefing/personal/${userId}` : '/briefing/personal';
      return request<TechnicianBriefingStatus>(path);
    },

    async generatePersonalBriefing(userId?: string) {
      const path = userId
        ? `/briefing/personal/${userId}/generate`
        : '/briefing/personal/generate';
      return request<{ briefing: TechnicianBriefing; unchanged?: boolean; shared?: boolean }>(
        path,
        { method: 'POST', body: JSON.stringify({}), kianUsageInline: true },
      );
    },
  };
}
